import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { api } from "../../lib/api";

function useDebounce(value, delay = 500) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const STATUS_BADGE = {
  active: "bg-green-100 text-green-700",
  grace: "bg-yellow-100 text-yellow-700",
  blocked: "bg-red-100 text-red-700",
  disabled: "bg-gray-100 text-gray-600",
};

export default function CreditLineList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [status, setStatus] = useState("");

  const fetchCreditLines = async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/credit-lines", {
        params: { page, limit: pagination.limit, search: debouncedSearch || undefined, status: status || undefined },
      });
      setRows(res.data?.data || []);
      setPagination(res.data?.pagination || { page, limit: 20, total: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load credit lines");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCreditLines(1);
  }, [debouncedSearch, status]);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h1 className="text-xl font-bold text-gray-800">Credit Lines</h1>
        <button
          type="button"
          onClick={() => navigate("/admin/credit-lines/issue")}
          className="rounded-lg bg-blue-600 text-white px-4 py-2 text-sm font-semibold hover:bg-blue-700 self-start"
        >
          Issue Credit Line
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          type="text"
          placeholder="Search by name, email or mobile"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm w-full sm:w-72"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm w-full sm:w-auto"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="grace">Grace Period</option>
          <option value="blocked">Blocked</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Credit Limit</th>
              <th className="px-4 py-3">Available Balance</th>
              <th className="px-4 py-3">Payment Term</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No credit lines found</td></tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-4 py-3">
                    {row.customer ? `${row.customer.first_name || ""} ${row.customer.last_name || ""}`.trim() : row.user_id}
                    <div className="text-xs text-gray-500">{row.customer?.email}</div>
                  </td>
                  <td className="px-4 py-3">INR {Number(row.credit_limit).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3">
                    {row.available_balance == null ? "—" : `INR ${Number(row.available_balance).toLocaleString("en-IN")}`}
                  </td>
                  <td className="px-4 py-3">{row.payment_term_days} days</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_BADGE[row.status] || ""}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/credit-lines/${row.id}`)}
                      className="text-blue-600 hover:underline font-semibold"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mt-4 text-sm text-gray-600">
        <span>Total: {pagination.total}</span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => fetchCreditLines(pagination.page - 1)}
            className="border rounded-lg px-3 py-1.5 disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={pagination.page * pagination.limit >= pagination.total}
            onClick={() => fetchCreditLines(pagination.page + 1)}
            className="border rounded-lg px-3 py-1.5 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
